import React, {
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { View } from "react-native";
import { Button, ButtonText } from "ui/gluestack/button";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { ReusableModal } from "ui/molecules/modal/ReusableModal";
import { RoomModel } from "app/services/api/types/locations/models/RoomModel";
import { RoomRequest } from "app/services/api/types/locations/requests/RoomRequest";

export interface EditRoomModalRef {
  show: (room?: RoomModel) => void;
  hide: () => void;
}

type RoomFormData = Omit<RoomRequest, "floor_id">;

interface EditRoomModalProps {
  onSave: (roomData: RoomFormData, roomId?: number) => void;
  isLoading?: boolean;
}

export const EditRoomModal = forwardRef<EditRoomModalRef, EditRoomModalProps>(
  ({ onSave, isLoading = false }, ref) => {
    const [isVisible, setIsVisible] = useState(false);
    const [room, setRoom] = useState<RoomModel | undefined>();

    const {
      control,
      handleSubmit,
      reset,
      formState: { isValid },
    } = useForm<RoomFormData>({
      defaultValues: {
        name: "",
        room_number: "",
        description: "",
      },
      mode: "onChange",
    });

    useImperativeHandle(
      ref,
      () => ({
        show: (showRoom?: RoomModel) => {
          setRoom(showRoom);
          setIsVisible(true);
        },
        hide: () => {
          setIsVisible(false);
        },
      }),
      [],
    );

    useEffect(() => {
      if (isVisible) {
        if (room) {
          reset({
            name: room.name,
            room_number: room.room_number,
            description: room.description,
          });
        } else {
          reset({
            name: "",
            room_number: "",
            description: "",
          });
        }
      }
    }, [isVisible, room, reset]);

    const handleSave = (data: RoomFormData) => {
      onSave(data, room?.id);
    };

    const handleClose = () => {
      reset();
      setIsVisible(false);
    };

    const canSave = isValid && !isLoading;

    return (
      <ReusableModal
        isVisible={isVisible}
        onClose={handleClose}
        title={room ? "Edit Room" : "Add Room"}
        hasCancelButton
      >
        <View className="space-y-4">
          <FormControlledInput
            id="room-name"
            name="name"
            label="Room Name"
            placeholder="e.g., Master Bedroom, Room 101"
            control={control}
            isRequired
          />

          <FormControlledInput
            id="room-number"
            name="room_number"
            label="Room Number"
            placeholder="e.g., 101, 102, 201"
            control={control}
            isRequired
          />

          <FormControlledInput
            id="room-description"
            name="description"
            label="Description"
            placeholder="Optional description for the room"
            control={control}
            isRequired={false}
          />
        </View>

        <View className="flex-row gap-3 mt-6">
          <Button
            variant="outline"
            onPress={handleClose}
            className="flex-1"
            disabled={isLoading}
          >
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            onPress={handleSubmit(handleSave)}
            className="flex-1"
            disabled={!canSave}
          >
            <ButtonText>
              {isLoading ? "Saving..." : room ? "Update" : "Add"}
            </ButtonText>
          </Button>
        </View>
      </ReusableModal>
    );
  },
);

EditRoomModal.displayName = "EditRoomModal";
